/**
 * RWBR — WHEN A DRIVER RACES OFF A DIFFERENT CARD.
 *
 * D-EZ-76. The 2025 set stopped greying out ratings on a single card and started
 * printing ALTERNATES, and its notes name all five, verbatim:
 *
 *   • Kyle Larson gets SMART *"in the Playoff races only"*.
 *   • William Byron *"gets HEAT in Playoff races only"*.
 *   • Bubba Wallace gets STAR★, but *"the 'starred' STAR quality only applies on
 *     tracks 2.0 miles or greater"*.
 *   • Austin Dillon gets HEAT★, but *"the 'starred' HEAT only applies on tracks
 *     less than one mile"*.
 *   • Shane van Gisbergen has two whole cards: *"the card with the 'R' on the
 *     hood for road courses, and the card with the 'O' on the hood for any other
 *     track."*
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠ THE ALTERNATE IS A DIFFERENCE, NOT A CARD, AND THAT IS THE WHOLE DESIGN.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * It was written the obvious way first — the alternate as a second set of
 * absolute numbers — and Howard put his finger on what that costs, 21 Aug 2026,
 * after being told the alternates would be dropped at the first winter:
 *
 *   *"keep the shape of the alternate but let the winter move it — so he stays
 *   better on road courses than on ovals forever, by roughly the same margin,
 *   but both cards drift up and down with his actual results."*
 *
 * Absolute numbers cannot do that. Van Gisbergen's printed "R" card is a 5 gas,
 * 3 brake, A crew, gold trophy — figures that describe how he drove in 2025.
 * Once a league's own winter has re-rated him those figures are about a driver
 * who no longer exists, and re-applying them would have him turning up at Sonoma
 * in 2029 as a gold-trophy A-crew car no matter what four seasons had said.
 *
 * Stored as a DIFFERENCE — +2 gas, +3 brake, two grades of crew, two trophies,
 * three qualities added and two of them starred — the same rule survives any
 * amount of re-rating. He is always that much better on a road course than he is
 * anywhere else, and both cards move with him. Being brilliant on road courses
 * is who he is; the 2025 numbers were only ever this year's measurement of it.
 *
 * ⚠ AND IT LIVES ON THE CARD, NOT IN THE SEASON'S OPTIONS. That is what makes it
 * free: a card travels through the winter (`offseason.ts` spreads the old card
 * into the new one), through a transfer, through the save slot, through the
 * archive. There is no path a card can take that the rule does not take with it,
 * and therefore no path anybody has to remember to plumb.
 *
 * ⚠ IT IS DATA, NOT A CALLBACK, for the same reason everything else on a card
 * is: a `DriverCard` has to survive `JSON.parse(JSON.stringify(state))`.
 */

import type { DriverCard, PitCrewGrade, Quality, Trophy } from './types.ts';

// ---------------------------------------------------------------------------
// When
// ---------------------------------------------------------------------------

/** What has to be true of a race for the alternate card to be the one he uses. */
export type CardCondition =
  /** Any race of the playoff rounds (27-36). */
  | { kind: 'playoffs' }
  /** A racetrack of at least this many miles. */
  | { kind: 'track-at-least'; miles: number }
  /** A racetrack under this many miles. */
  | { kind: 'track-under'; miles: number }
  /** A road course, whatever its length. */
  | { kind: 'road-course' };

/** Where a race is, for deciding whether an alternate card applies. */
export interface RaceConditions {
  lengthMiles: number | null;
  category: string;
  /** True for any race of the playoff rounds. */
  playoff: boolean;
}

/**
 * ⚠ A CONDITION ON A LENGTH NOBODY PRINTED IS FALSE, NOT TRUE.
 *
 * Six of the older track cards have no `lengthMiles`. Wallace's star is worth
 * having and Dillon's is worth having, so the failure that costs a player
 * something he was promised is tempting to avoid — but the failure that GIVES
 * him something the card never granted is worse, because nothing in the game
 * would ever show it. Unknown length means the base card, every time.
 */
export function conditionHolds(when: CardCondition, at: RaceConditions): boolean {
  switch (when.kind) {
    case 'playoffs':
      return at.playoff;
    case 'road-course':
      return at.category === 'Road Course';
    case 'track-at-least':
      return at.lengthMiles !== null && at.lengthMiles >= when.miles;
    case 'track-under':
      return at.lengthMiles !== null && at.lengthMiles < when.miles;
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// How much
// ---------------------------------------------------------------------------

/**
 * THE LADDERS, worst to best. A trophy delta of +2 means "two trophies better
 * than whatever he is now", which is the only phrasing that still means
 * something after a winter has moved him.
 */
export const TROPHY_LADDER: readonly Trophy[] = [null, 'bronze', 'silver', 'gold'];
export const GRADE_LADDER: readonly PitCrewGrade[] = ['E', 'D', 'C', 'B', 'A'];

/**
 * WHAT A CARD IS ALLOWED TO SAY, and these are the game's own limits rather
 * than round numbers.
 *
 * `gas` and `escape` and `fastPit` are the spread of values `driverGen.ts`'s
 * calibrated tables can actually produce; `brake` is 0-3 because the 2025 set
 * prints a 3 (van Gisbergen's road card) where the generator stops at 2.
 *
 * ⚠ CLAMPING IS NOT A ROUNDING ERROR, IT IS THE CEILING DOING ITS JOB. A driver
 * whose ordinary card has drifted up to a 5 gas cannot be a 7 gas on a road
 * course, and the right answer there is that his road-course advantage in that
 * one field has been earned everywhere else instead. The margin narrows as he
 * gets better, which is exactly what happens to real specialists.
 */
export const CARD_BOUNDS = {
  gasPedal: [2, 5],
  brakePedal: [0, 3],
  escape: [2, 11],
  fastPit: [2, 10],
} as const;

const clamp = (n: number, [lo, hi]: readonly [number, number]): number => Math.min(hi, Math.max(lo, n));

/**
 * The difference between a driver's ordinary card and his alternate.
 *
 * Every numeric field is a SIGNED STEP applied to whatever the card says today.
 * Absent means unchanged — an alternate that only speaks about qualities leaves
 * every number exactly where the winter left it.
 */
export interface CardDelta {
  gasPedal?: number;
  brakePedal?: number;
  escape?: number;
  fastPit?: number;
  /** Steps up `TROPHY_LADDER`. */
  trophy?: number;
  /** Steps up `GRADE_LADDER`. */
  pitCrewGrade?: number;
  addQualities?: Quality[];
  dropQualities?: Quality[];
  addStarred?: Quality[];
  dropStarred?: Quality[];
  /** An absolute override, because an exemption is not a quantity. */
  dnfExempt?: boolean;
}

/** The alternate card as it is stored ON the driver's card. */
export interface AlternateCard {
  /** What the set calls it — "Road Courses", "Playoffs". For the ledger. */
  label: string;
  when: CardCondition;
  delta: CardDelta;
}

const isEmptyDelta = (d: CardDelta): boolean =>
  Object.values(d).every((v) => v === undefined || (Array.isArray(v) && v.length === 0) || v === 0);

/**
 * Read a printed alternate as a difference from the card beside it.
 *
 * This is how a TRANSCRIPTION becomes a rule. `roster-2025.ts` records what is
 * printed — two sets of absolute numbers, because that is what is on the
 * cardboard — and this turns the pair into the thing the engine keeps.
 *
 * ⚠ RUN ONCE, WHEN THE CARD IS BUILT, and never again. Diffing a re-rated card
 * against a stored alternate later would measure the winter as though it were
 * part of the printed difference.
 */
export function deltaFrom(base: DriverCard, alt: Partial<DriverCard>): CardDelta {
  const num = (a: number | undefined, b: number): number | undefined =>
    a === undefined || a === b ? undefined : a - b;
  const rung = <T,>(ladder: readonly T[], a: T | undefined, b: T): number | undefined => {
    if (a === undefined) return undefined;
    const to = ladder.indexOf(a);
    const from = ladder.indexOf(b);
    if (to === -1 || from === -1 || to === from) return undefined;
    return to - from;
  };
  const setDiff = (a: readonly Quality[] | undefined, b: readonly Quality[]) => ({
    added: a ? a.filter((q) => !b.includes(q)) : [],
    dropped: a ? b.filter((q) => !a.includes(q)) : [],
  });

  const q = setDiff(alt.qualities, base.qualities);
  const s = setDiff(alt.starred, base.starred);

  const delta: CardDelta = {
    ...(num(alt.gasPedal, base.gasPedal) !== undefined ? { gasPedal: num(alt.gasPedal, base.gasPedal) } : {}),
    ...(num(alt.brakePedal, base.brakePedal) !== undefined ? { brakePedal: num(alt.brakePedal, base.brakePedal) } : {}),
    ...(num(alt.escape, base.escape) !== undefined ? { escape: num(alt.escape, base.escape) } : {}),
    ...(num(alt.fastPit, base.fastPit) !== undefined ? { fastPit: num(alt.fastPit, base.fastPit) } : {}),
    ...(rung(TROPHY_LADDER, alt.trophy, base.trophy) !== undefined
      ? { trophy: rung(TROPHY_LADDER, alt.trophy, base.trophy) }
      : {}),
    ...(rung(GRADE_LADDER, alt.pitCrewGrade, base.pitCrewGrade) !== undefined
      ? { pitCrewGrade: rung(GRADE_LADDER, alt.pitCrewGrade, base.pitCrewGrade) }
      : {}),
    ...(q.added.length ? { addQualities: q.added } : {}),
    ...(q.dropped.length ? { dropQualities: q.dropped } : {}),
    ...(s.added.length ? { addStarred: s.added } : {}),
    ...(s.dropped.length ? { dropStarred: s.dropped } : {}),
    ...(alt.dnfExempt !== undefined && alt.dnfExempt !== base.dnfExempt ? { dnfExempt: alt.dnfExempt } : {}),
  };
  return delta;
}

// ---------------------------------------------------------------------------
// Applying it
// ---------------------------------------------------------------------------

/**
 * The card this driver races off today.
 *
 * Applied to whatever his card says NOW, which is the entire point: re-rate him
 * for four seasons and the road-course advantage is still the same difference,
 * measured from wherever he has got to.
 */
export function alternateOf(card: DriverCard, at: RaceConditions): DriverCard {
  const alt = card.altCard;
  if (!alt || !conditionHolds(alt.when, at)) return card;
  const d = alt.delta;
  if (isEmptyDelta(d)) return card;

  /*
    ⚠ A STAR CARRIES ITS QUALITY WITH IT, and this was found by running a whole
    season and a winter rather than by thinking about it.

    Van Gisbergen's road card is SMART★ and HEAT★. Both of those qualities are
    on his ordinary 2025 card too, so the printed difference is only the two
    stars — and for his rookie season that is a complete description. Then the
    winter re-rated him, SAG/7 dealt him a different set of qualities, SMART and
    HEAT were not among them, and his road-course card came back with no stars
    at all: the star was filtered off a quality he no longer held.

    Which is the wrong answer twice over. The set gave him those stars because
    of how he drove on road courses; losing them because of what happened on
    ovals inverts the rule. And it fails silently — nothing throws, he simply
    stops getting the second challenge, at Sonoma and nowhere else.

    So a star in `addStarred` GRANTS the quality if he does not have it. That is
    also what the cardboard says: the "R" hood prints SMART★, and a card that
    prints a starred quality prints the quality.
  */
  const starGrants = (d.addStarred ?? []).filter((q: Quality) => !card.qualities.includes(q));
  const qualities = [
    ...card.qualities.filter((q) => !(d.dropQualities ?? []).includes(q)),
    ...(d.addQualities ?? []).filter((q: Quality) => !card.qualities.includes(q)),
    ...starGrants.filter((q: Quality) => !(d.addQualities ?? []).includes(q)),
  ];
  /*
    And the reverse still holds: `challenge.ts` reads `starred` to grant a second
    challenge and `qualities` to decide whether he is in the called group at all.
    The two must never disagree, so the list is filtered against the qualities
    that survived — which after the grant above can only remove a star that
    `dropQualities` deliberately took the quality away for.
  */
  const starred = [
    ...card.starred.filter((q) => !(d.dropStarred ?? []).includes(q)),
    ...(d.addStarred ?? []).filter((q: Quality) => !card.starred.includes(q)),
  ].filter((q) => qualities.includes(q));

  const step = <T,>(ladder: readonly T[], now: T, by: number | undefined): T => {
    if (by === undefined) return now;
    const i = ladder.indexOf(now);
    if (i === -1) return now;
    return ladder[Math.min(ladder.length - 1, Math.max(0, i + by))];
  };

  return {
    ...card,
    qualities,
    starred,
    gasPedal: d.gasPedal === undefined ? card.gasPedal : clamp(card.gasPedal + d.gasPedal, CARD_BOUNDS.gasPedal),
    brakePedal:
      d.brakePedal === undefined ? card.brakePedal : clamp(card.brakePedal + d.brakePedal, CARD_BOUNDS.brakePedal),
    escape: d.escape === undefined ? card.escape : clamp(card.escape + d.escape, CARD_BOUNDS.escape),
    fastPit: d.fastPit === undefined ? card.fastPit : clamp(card.fastPit + d.fastPit, CARD_BOUNDS.fastPit),
    trophy: step(TROPHY_LADDER, card.trophy, d.trophy),
    pitCrewGrade: step(GRADE_LADDER, card.pitCrewGrade, d.pitCrewGrade),
    ...(d.dnfExempt !== undefined ? { dnfExempt: d.dnfExempt } : {}),
  };
}

/**
 * The cards a whole field races off today.
 *
 * A card with no alternate, or one whose condition does not hold, comes back as
 * the identical object — so the common case costs one property read per driver
 * and allocates nothing.
 */
export function applyAlternateCards(cards: readonly DriverCard[], at: RaceConditions): DriverCard[] {
  return cards.map((card) => (card.altCard ? alternateOf(card, at) : card));
}

/** One line for a screen: "Road Courses — +2 gas, +3 brake, A crew, gold". */
export function describeDelta(d: CardDelta): string {
  const bits: string[] = [];
  const signed = (n: number) => (n > 0 ? `+${n}` : String(n));
  if (d.gasPedal) bits.push(`${signed(d.gasPedal)} gas`);
  if (d.brakePedal) bits.push(`${signed(d.brakePedal)} brake`);
  if (d.escape) bits.push(`${signed(d.escape)} escape`);
  if (d.fastPit) bits.push(`${signed(d.fastPit)} fast pit`);
  if (d.pitCrewGrade) bits.push(`${Math.abs(d.pitCrewGrade)} crew grade${Math.abs(d.pitCrewGrade) === 1 ? '' : 's'} ${d.pitCrewGrade > 0 ? 'better' : 'worse'}`);
  if (d.trophy) bits.push(`${Math.abs(d.trophy)} trophy step${Math.abs(d.trophy) === 1 ? '' : 's'} ${d.trophy > 0 ? 'up' : 'down'}`);
  for (const q of d.addQualities ?? []) bits.push(`+${q}`);
  for (const q of d.dropQualities ?? []) bits.push(`−${q}`);
  for (const q of d.addStarred ?? []) bits.push(`${q}★`);
  for (const q of d.dropStarred ?? []) bits.push(`${q} unstarred`);
  return bits.join(', ');
}
